import React, { useState, useRef, useEffect } from 'react';

// ── Ant Design ────────────────────────────────────────────────────────────────
import {
  Alert,
  Space,
  Modal,
  Typography,
} from 'antd';
import PageHeader from 'Components/PageHeader';
import { PageContainerStyled } from 'styles/common.styles';
import SbButton from 'uiCore/SbButton/SbButton';
import SbSelect from 'uiCore/SbSelect/SbSelect';
import {
  CloseOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from '@ant-design/icons';

// ── Business-logic imports (identical to V1) ──────────────────────────────────
import useJobsStore from 'Store/useJobsStore';
import useTourStore from 'Store/useTourStore';
import SbTour from 'uiCore/SbTour/SbTour';
import TourStep from 'uiCore/SbTour/TourStep';
import useReadyTour from 'uiCore/SbTour/useReadyTour';
import { JOB_DETAILS_TOOLBAR_STEP, CROSS_PAGE_TOUR_PHASE } from 'Pages/JobsLibraryV2/JobsLibraryV2.constants';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import useJobDetailsV2 from 'Hooks/useJobDetailsV2';
import useFullscreenToggle from 'Hooks/useFullscreenToggle';
import SbDownloadMarkdownPdfV2 from 'Components/HumanEditorV2/SbDownloadMarkdownPdfV2';
import { buildFieldConfigMap, decodeHtmlString, isMarkdown, } from 'Utils/formatting';
import { versionOptionLabel, getLatestVersionRow, getLatestNonDraftRow, isDraftVersionOption } from 'Utils/versionUtils';
import { getPageState, PAGE_STATE } from 'Utils/jobDetailsPageState';
import useAuthStore from 'Store/useAuthStore';
import analytics, { ANALYTICS_EVENTS } from 'Utils/analytics';
import useEntitlementsStore from 'Store/useEntitlementsStore';
import { useHasAccess } from 'Routes/hooks/useHasAccess';
import { submitForApproval, saveRevisionDraft, selfPublishJd, getSBJobDescription, delegateJd, removeJdDelegate, shareJdDrafts, unshareJdDrafts, getDraftFeedback, resolveDraftFeedback, reportJdDownload } from 'Api/apiService';
import Permission from 'Components/Permission';
import SbJdCreationDrawer from 'Components/SbJdCreationDrawerV2/SbJdCreationDrawerV2';
import { VIEWMODE } from 'constants/markdown.constants';
import { JD_STATUSES, JD_STATUSES_EDITABLE, JD_TABS, WORKFLOW_STATUS, JD_ACTION, COMPARE_ENTRY, getReviseTooltip, confirmReviseIfManuallyEdited, getDraftNotice, DRAFT_NOTICE_STYLES } from 'constants/jobs.constants';
import CoreMarkdownEditor from 'Components/SbMarkdownEditor/CoreMarkdownEditor';
import { isNewDiffCheckerFeatureEnabled } from 'Utils/featureFlag';
import ComparisonsummaryIcon from 'Components/SBLayoutIcon/ComparisonsummaryIcon';
// ── Styled components ─────────────────────────────────────────────────────────
import {
  LayoutWrapperStyled,
  PageWrapperStyled,
  WhiteContainerStyled,
  HeaderSectionStyled,
  HeaderLeftStyled,
  EditModeHeadingStyled,
  VersionBlockStyled,
  VersionMetaStyled,
  VersionPeopleStyled,
  ScrollableContentStyled,
  DisclaimerStyled,
  BlurredContentStyled,
  FreeTrialPopupStyled,
  StatusBadgeWrapperStyled,
  EditModeWrapperStyled,
  ComingSoonStyled,
  TabWrapper,
  ScrollAreaStyled,
  PageSpinnerStyled,
  JdSpinnerStyled,
} from 'Pages/SbJobDetailsV2/SbJobDetailsV2.styles';
import SbTabs from 'uiCore/SbTabs/SbTabs';
import SbJobCompareV2 from 'Pages/JobCompareV2/SbJobCompareV2';
import SbDiffCheckerV3 from 'Components/SbDiffCheckerV3/SbDiffCheckerV3';
import SbJdHumanEditorV2 from 'Components/HumanEditorV2/SbJdHumanEditorV2';
import SbModal from 'uiCore/SbModal/SbModal';
import DifferenceCheckerIcon from 'Components/SBLayoutIcon/DifferenceCheckerIcon';
import EditIcon from 'Components/SBLayoutIcon/EditIcon';
import ReviseIcon from 'Components/SBLayoutIcon/ReviseIcon';
import DownloadIcon from 'Components/SBLayoutIcon/DownloadIcon';
import PdfIcon from 'Components/SBLayoutIcon/PdfIcon';
import DocxIcon from 'Components/SBLayoutIcon/DocxIcon';
import { SbSpin, SbEmpty, SbTooltip, SbInfoTooltip, TooltipTriggerStyled, SbNotification } from 'uiCore';
import {
  SAVE_AS_DRAFT_LABEL,
  TOOLTIP_SAVE_AS_DRAFT,
  TOOLTIP_MAKE_CHANGES_FIRST,
  DRAFT_CANCEL_CONFIRM,
  DRAFT_SAVE_FAILED_TITLE,
  getEditorButtonRules,
  isHumanEditedDraft,
  getPreviousStoredVersionId,
  shouldConfirmCancel,
  getStaleCheckValues,
  buildDraftSavePayload,
  pickSavedDraft,
  getDraftSavedToast,
  getDraftSaveError,
} from 'Utils/saveAsDraft';
import {
  SEND_FOR_APPROVAL_LABEL,
  PUBLISH_LABEL,
  TOOLTIP_SEND_MAIN,
  TOOLTIP_PUBLISH_MAIN,
  TOOLTIP_SEND_UNCHANGED,
  TOOLTIP_PUBLISH_UNCHANGED,
  PUBLISH_BOX_ACTION,
  SEND_FAILED_TITLE,
  PUBLISH_FAILED_TITLE,
  getSentToast,
  getPublishedToast,
  hadSavedDraft,
  isUnchangedUnpublished,
  buildEditorSendPayload,
  buildEditorPublishPayload,
  buildSelfPublishPayload,
  getSendOrPublishError,
} from 'Utils/sendAndPublish';
import AssignApproverModal from 'Components/SendAndPublish/AssignApproverModal';
import PublishWithoutReviewModal from 'Components/SendAndPublish/PublishWithoutReviewModal';
import JdCurrentOwnerRow from 'Components/JdOwnership/JdCurrentOwnerRow';
import JdDelegatedToRow from 'Components/JdOwnership/JdDelegatedToRow';
import DelegatePickerModal from 'Components/JdOwnership/DelegatePickerModal';
import JdSharedWithRow from 'Components/JdOwnership/JdSharedWithRow';
import SharePickerModal from 'Components/JdOwnership/SharePickerModal';
import DraftCommentsPanel from 'Components/DraftFeedback/DraftCommentsPanel';
import {
  COMMENT_RESOLVED_TOAST, TOOLTIP_DIFF_FEEDBACK, getCommentAuthors, getDiscardConfirm, getFeedbackError,
} from 'Utils/draftFeedback';
import {
  SHARE_FAILED_TITLE, getShareError, getSharedToast, getUnsharedToast,
  DELEGATION_FAILED_TITLE, NOT_HOLDER, NOT_HOLDER_MESSAGE, NOT_HOLDER_TITLE, getDelegatedToast, getDelegationError,
  getTakeBackConfirm, getRelinquishConfirm, getStopSharingConfirm, getUnpublishConfirmMessage,
  getNotHolderTooltip, getRelinquishedToast, getTakenBackToast, getUnclaimedTooltip, isJdHolder, isNotHolderError,
  showsEditAndRevise,
} from 'Utils/jdOwnership';
import { useHasPermission } from 'Hooks/useHasPermission';
import Paragraph from 'antd/es/typography/Paragraph';




// ─────────────────────────────────────────────────────────────────────────────

const TOOLTIP_VERSION_SELECTOR = "Switch between different versions of this job description. Each revision or AI regeneration creates a new version.";
const TOOLTIP_MODIFIED_ON = "Shows when this version was last modified and by whom.";
const TOOLTIP_COMPARISON_SUMMARY = "Compare any two published job descriptions from the library.";
const TOOLTIP_DIFF_APPROVE = "Approve the proposed changes to this job description.";
const TOOLTIP_DIFF_COMPARE = "Compare versions of this job description from the last published version to the latest.";
const TOOLTIP_EDIT_LOCKED_BY_OTHER = 'This JD is currently being edited by another user and cannot be modified right now.';
const TOOLTIP_EDIT_SURGICAL = 'Open surgical edit mode. You can only revise the sections flagged by the approver. Locked sections cannot be changed.';
const TOOLTIP_EDIT_DEFAULT = 'Edit this job description manually. Opens the full markdown editor. Not available when JD is Published or Pending Approval.';
// Keep this exact string (space and all) — `from` is a breakdown property on
// the existing Mixpanel boards, which already hold historical Job Details
// events under this value.
const ANALYTICS_FROM = 'job details';
const JD_EVENTS = ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS;

const TOOLTIP_DOWNLOAD_JD = "Download this job description as a PDF or DOCX file.";
const TOOLTIP_DOWNLOAD_PDF = "Download this job description as a PDF";
const TOOLTIP_DOWNLOAD_DOCX = "Download this job description as a Docx";
const TOOLTIP_DOWNLOAD_UNPUBLISHED = "Download this job description as a PDF. Once the JD is published, DOCX becomes available";
const TOOLTIP_FULLSCREEN_EXIT = "Exit Fullscreen";
const TOOLTIP_FULLSCREEN_ENTER = "Fullscreen";
const TOOLTIP_UNPUBLISH = "Unpublish this JD so it can be edited or revised. It will no longer be visible as a live JD until republished. You stay its owner.";
const TOOLTIP_OLD_VERSION = "You are viewing an older version. Switch to the latest version to make changes.";
const TOOLTIP_STATUS_BLOCKED = "This action is not available for the JD's current status.";
const TOOLTIP_PERMISSIONS_UNAVAILABLE = "Couldn't check your permissions. Refresh to try again.";
const TOOLTIP_REVISE_IN_REVIEW = "You can't revise during a review cycle";
const TOOLTIP_PUBLISH_IN_REVIEW = "You can't publish during a review cycle";
const TOOLTIP_SEND_IN_REVIEW = "You can't send for approval during a review cycle";
const TOOLTIP_PUBLISH_MID_REVIEW_APPROVER = "Can't be published in the middle of a review cycle. Approve all changes to Publish.";
const TOOLTIP_PUBLISH_MID_REVIEW_NOT_OWNER = "Only the assigned approver can publish this JD, after approving all changes.";

const { Title, Text } = Typography;

// Where Send for Approval / Publish was clicked: the editor (edits saved and sent or published in one request)
// or the main page (the latest version as it is)
const ACTION_FROM = { EDITOR: 'editor', MAIN: 'main' };

const SbJobDetailsV2 = () => {
  const { jobId, sbUniqueId } = useParams();

  const location = useLocation();
  const { viewMode, from: compareEntryFrom } = location.state || {};
  const email = useAuthStore((s) => s.email);
  const orgMetaData = useAuthStore((s) => s.orgMetaData);
  const entitlements = useEntitlementsStore((s) => s.entitlements);
  const { hasAccess } = useHasAccess();

  const canViewSkillsBridger = hasAccess('skillsbridger', ['view']);
  const [value, setValue] = useState('');
  const editorRef = useRef(null);
  const navigate = useNavigate();

  // ── Jobs Zustand store (V2: fine-grained selectors) ─────────────────────────
  const selectedJob = useJobsStore((state) => state.selectedJob);
  const isDrawerOpen = useJobsStore((state) => state.isDrawerOpen);
  const toggleDrawer = useJobsStore((state) => state.toggleDrawer);
  const setPendingJdId = useJobsStore((state) => state.setPendingJdId);
  const jdActionSelection = useJobsStore((state) => state.jdActionSelection);
  const selectedJdTab = useJobsStore((state) => state.selectedJdTab);
  const setSelectedJdTab = useJobsStore((state) => state.setSelectedJdTab);
  const setSelectedJob = useJobsStore((state) => state.setSelectedJob);
  const compareViewSelection = useJobsStore((state) => state.compareViewSelection);
  const setCompareViewSelection = useJobsStore((state) => state.setCompareViewSelection);
  const setJdActionSelection = useJobsStore((state) => state.setJdActionSelection);

  // ── Local state ─────────────────────────────────────────────────────────────
  const [isHumanEditing, setIsHumanEditing] = useState(false);
  const [selectedApprover, setSelectedApprover] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('jd');
  const [isEditable, setIsEditable] = useState(false);
  // Send for Approval / Publish: where the Send for Approval page and the "Publish without a review?" box were
  // opened from (ACTION_FROM.EDITOR or ACTION_FROM.MAIN), null when closed
  const [assignApproverFrom, setAssignApproverFrom] = useState(null);
  const [publishBoxFrom, setPublishBoxFrom] = useState(null);
  // Save as Draft (editor): the Cancel popup, the save in flight, the content under a human-edited draft
  // (for the button rules), and the ids/timestamp the server returned for the last draft saved here
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [previousContent, setPreviousContent] = useState(null);
  const lastSavedDraftRef = useRef(null);
  const { isFullscreen, handleFullscreenToggle } = useFullscreenToggle();
  const [confirmation, setConfirmation] = useState({
    show: false,
    message: '',
    actionText: '',
    action: () => { },
  });

  // ── useJobDetails hook ──────────────────────────────────────────────────────
  const {
    versionData,
    versionDropdownOptions,
    loading,
    error,
    currentOwner,
    assignedApproverName,
    reassignedApproverEmail,
    publishedJDfiles,
    selectedJdVersion,
    setSelectedJdVersion,
    setEditableMarkdown,
    editableMarkdown,
    blurredJD,
    jdPublished,
    handleSbUniqueIdChange,
    unPublishJd,
    saveEditedJd,
    toastMessage,
    showToast,
    setShowToast,
    mongoWorkflowStatus,
    reviewHistory,
    refreshData,
    sbJdStatus,
    rootOwnerEmail,
    jdOwnership,
    jdInputType,
  } = useJobDetailsV2();

  // ── Jobs Library guided tour: the cross-page toolbar step ──────────────────
  // When Jobs Library hands off (handoffPhase = job-details), spotlight the
  // toolbar once it's visible and the page has settled, then set the finish
  // phase and navigate back so Jobs Library resumes at its Finish step.
  const toolbarRef = useRef(null);
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  const jobsToolbarStepActive =
    crossPageTourPhase === CROSS_PAGE_TOUR_PHASE.JOB_DETAILS &&
    !isEditable && !loading && !isHumanEditing;
  const { open: toolbarTourOpen, remountKey: toolbarTourKey } = useReadyTour({
    active: jobsToolbarStepActive,
    ready: !loading,
  });
  const endJobsToolbarStep = () => {
    setCrossPageTourPhase(CROSS_PAGE_TOUR_PHASE.FINISH);
    navigate('/jobs-library');
  };


  const selectedVersionData = versionData.find(
    v => String(v.version) === String(selectedJdVersion),
  );
  // Title/requisition/org fields are the same on every version, but /api/versions only returns
  // the most recent ones — so the header falls back to the latest row for older versions.
  const headerVersionData = selectedVersionData ?? versionData[0];
  // the version on screen (what a download downloads), for the audit log
  const shownSbUniqueId = selectedVersionData?.sb_unique_id ?? sbUniqueId;

  // Dropdown lists the approval-history range (P-1 through current), falling
  // back to versionData if that list is unavailable.
  const versionSelectSource = versionDropdownOptions?.length ? versionDropdownOptions : versionData;
  const versionSelectOptions = versionSelectSource.map(v => ({
    value: String(v.version),
    // v.tag comes from /approval/history_list. The versionData fallback has no tag at all (no
    // "(undefined)"), and a HISTORICAL version shows its source only, e.g. "V1.00 (AI-GENERATED)".
    label: versionOptionLabel(v),
    // drafts reach only whoever may see them (the backend filters); the list marks them "private"
    isDraft: isDraftVersionOption(v),
  }));
  // The dropdown list marks each draft "private" (wireframe); the selected value shows the label only
  const renderVersionOption = (option) => (
    <span>
      {option.label}
      {option.data?.isDraft && <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>private</Text>}
    </span>
  );

  // ── Derived values ───────────────────────────────────────────────────────────
  const getLatestFamilyStatus = () => {
    if (mongoWorkflowStatus) return mongoWorkflowStatus;
    return selectedJob?.jd_status || WORKFLOW_STATUS.LOADING_STATUS;
  };

  const latestFamilyStatus = getLatestFamilyStatus();
  const mongoOwnerUserName = (rootOwnerEmail || '').toLowerCase().replace(/\s+/g, '').replace(/\./g, '');
  const currentUserName = email.split("@")[0].toLowerCase().replace(/\./g, '');
  // Who may act comes from the backend: the JD's holder (its owner, or its delegate while delegated), or anyone on
  // an Unclaimed JD. The name match is only a fallback for responses that don't carry it (e.g. license preview).
  const isLockedByOther =
    latestFamilyStatus !== JD_STATUSES.PUBLISHED &&
    (jdOwnership
      ? !isJdHolder(jdOwnership) && !jdOwnership.isUnowned
      : mongoOwnerUserName !== '' && mongoOwnerUserName.toLowerCase() !== currentUserName.toLowerCase());

  // ── Editor button rules (Save as Draft design, Button rules): judged by what each button would store,
  // from the content (never click history). The editor always opens the latest version (Edit requires it).
  const latestVersionRow = getLatestVersionRow(versionData);
  const editingDraft = sbJdStatus === JD_STATUSES.DRAFT;
  const editingHumanDraft = isHumanEditedDraft(sbJdStatus, jdInputType);
  const previousStoredId = getPreviousStoredVersionId(latestVersionRow, editingHumanDraft);
  const editorRules = getEditorButtonRules({
    content: value,
    loadedContent: editableMarkdown,
    isDraft: editingDraft,
    isHumanDraft: editingHumanDraft,
    previousContent: editingHumanDraft ? previousContent : null,
  });

  // Tooltip for an action the backend says this user can't take on this JD; null = not blocked.
  const blockedReason = (action) => {
    if (!jdOwnership) return null;
    const permission = jdOwnership.permissions?.[action];
    // Fail closed: the backend answered but couldn't decide this action (e.g. its check errored)
    if (!permission) return TOOLTIP_PERMISSIONS_UNAVAILABLE;
    if (permission.allowed) return null;
    if (permission.reason === 'OLD_VERSION') return TOOLTIP_OLD_VERSION;
    // Not the holder (wireframe): who owns the JD, or the shared-draft text for someone it is shared with
    if (permission.reason === NOT_HOLDER) {
      return getNotHolderTooltip(jdOwnership.ownerName || currentOwner, jdOwnership.isSharedWith,
        jdOwnership.delegateEmail ? (jdOwnership.delegateName || jdOwnership.delegateEmail) : '');
    }
    return TOOLTIP_STATUS_BLOCKED;
  };

  const config = buildFieldConfigMap(orgMetaData);

  // ── Tooltip entitlement guards (REQ-442 §5.2–5.4) ───────────────────────────
  const canEditJd = useHasPermission('jobs_library-job_details:edit');
  const canReviseJd = useHasPermission('jobs_library-job_details:revise');
  const canCompareData = useHasPermission('jobs_library:compare_data');
  const canViewJd = useHasPermission('jobs_library-job_details:view');
  const canPublishJd = useHasPermission('jobs_library-job_details:publish');
  const canUnpublishJd = useHasPermission('jobs_library-job_details:unpublish');
  const canViewVersion = useHasPermission('jobs_library-version:view');

  const hasApproveEntitlement = entitlements
    ?.find((e) => e.name === 'jobs_library-job_details')
    ?.entitlements?.some((e) => e === 'approver' || e === '*') ?? false;

  // Whether the version on screen is the latest one this viewer may see: the backend's answer
  // (is_latest on the /versions rows), not a sort of version numbers here. Edit / Revise / Send for
  // Approval / Publish act on the latest version only.
  const isViewingLatestVersion = selectedVersionData?.is_latest === true;

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const formatJdDate = jdDate => {
    if (!jdDate?.$date) return null;
    return new Date(jdDate.$date).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };


  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleTabClick = tabKey => {
    analytics.trackSelectedJdTab({ from: ANALYTICS_FROM, email, description: JD_EVENTS.SELECTED_JD_TAB });
    if (tabKey === 'skills-bridger' && canViewSkillsBridger) {
      navigate('/skills-bridger');
      return;
    }
    setActiveTab(tabKey);
  };

  const proceedWithEdit = () => {
    if (latestFamilyStatus === JD_STATUSES.PUBLISHED) {
      Modal.warning({ title: 'This JD is currently published. Please unpublish it before editing.' });
      return;
    }
    if (isNewDiffCheckerFeatureEnabled()) {
      if (latestFamilyStatus === JD_STATUSES.NEEDS_REVISION) {
        setIsHumanEditing(true);
        setIsEditable(false);
      } else if (JD_STATUSES_EDITABLE.includes(latestFamilyStatus)) {
        setIsEditable(true);
        setIsHumanEditing(false);
        setValue(editableMarkdown);
      }
    } else {
      setIsEditable(true);
      setIsHumanEditing(false);
      setValue(editableMarkdown);
    }
  };

  // Edit is disabled unless the backend says this user may edit (the holder, or anyone on an Unclaimed JD), so
  // there is no takeover path here: an Unclaimed JD is claimed by saving, never by opening the editor.
  const handleEditClick = () => {
    analytics.trackEditJD({ from: ANALYTICS_FROM, email, description: JD_EVENTS.EDIT_JD });
    proceedWithEdit();
  };

  const handleCancelClick = () => {
    setIsEditable(false);
    setIsHumanEditing(false);
  };

  // The JD changed since it was opened: offer to reload it (the edits stay until they choose)
  const offerReload = (title, message) => {
    Modal.confirm({
      title,
      content: message,
      okText: 'Reload',
      cancelText: DRAFT_CANCEL_CONFIRM.keepEditingLabel,
      onOk: async () => {
        lastSavedDraftRef.current = null;
        handleCancelClick();
        setValue('');
        await refreshData();
      },
    });
  };

  // ── Delegation (Ownership design, part 2; wireframe "Delegated to" row) ────────────────────────────────
  // The actor gets a short confirmation; the person affected gets a bell notification from the server. A refusal
  // (e.g. someone else changed it meanwhile) says why and reloads the page so it shows who has control now.
  const [delegatePickerOpen, setDelegatePickerOpen] = useState(false);
  const [delegationBusy, setDelegationBusy] = useState(false);
  // Reload to the latest version this user may see now (a draft on screen may no longer be theirs to see) and
  // point the address at it
  const reloadAfterControlChange = async () => {
    setDelegatePickerOpen(false);
    const shownId = await refreshData();
    if (shownId) navigate(`/jobs-library/job-details/${jobId}/${shownId}`, { replace: true });
  };
  const runDelegationChange = async (change, toast) => {
    setDelegationBusy(true);
    try {
      await change();
      SbNotification.success(toast);
      await reloadAfterControlChange();
    } catch (err) {
      const failure = getDelegationError(err);
      SbNotification.error(DELEGATION_FAILED_TITLE, failure.message);
      if (failure.reload) await reloadAfterControlChange();
    } finally {
      setDelegationBusy(false);
    }
  };
  const handleDelegate = (email, name) => runDelegationChange(
    () => delegateJd(jobId, email), getDelegatedToast(name || email));
  // Take back asks first (the delegate loses control at once)
  const handleTakeBack = () => {
    const name = jdOwnership?.delegateName || jdOwnership?.delegateEmail || '';
    Modal.confirm({
      ...getTakeBackConfirm(name),
      onOk: () => runDelegationChange(
        () => removeJdDelegate(jobId), getTakenBackToast(headerVersionData?.sb_requisition_number)),
    });
  };
  const handleRelinquish = () => {
    const ownerName = jdOwnership?.ownerName || currentOwner;
    Modal.confirm({
      ...getRelinquishConfirm(ownerName),
      onOk: () => runDelegationChange(() => removeJdDelegate(jobId), getRelinquishedToast(ownerName)),
    });
  };

  // ── Draft sharing (Ownership design, part 3; wireframe "Draft shared with" row) ──────────────────────
  // Same pattern: the actor's confirmation, the server's bell notifications, a refusal says why and reloads.
  const [sharePickerOpen, setSharePickerOpen] = useState(false);
  const [sharingBusy, setSharingBusy] = useState(false);
  const runShareChange = async (change, toast) => {
    setSharingBusy(true);
    try {
      await change();
      SbNotification.success(toast);
      setSharePickerOpen(false);
      await reloadAfterControlChange();
    } catch (err) {
      const failure = getShareError(err);
      SbNotification.error(SHARE_FAILED_TITLE, failure.message);
      if (failure.reload) {
        setSharePickerOpen(false);
        await reloadAfterControlChange();
      }
    } finally {
      setSharingBusy(false);
    }
  };
  // ── Draft Feedback (Ownership design, part 3) ─────────────────────────────────────────────────────────
  // The person a private latest draft is shared with comments from Version Compare → Draft Feedback; the owner and
  // the delegate read the open comments (the draft bar names who gave them) and resolve them in the editor.
  const isSharedPerson = jdOwnership?.isSharedWith === true && !jdOwnership?.isOwner && !jdOwnership?.isDelegate;
  const latestIsPrivateDraft = jdOwnership?.familyStatus === JD_STATUSES.DRAFT;
  const seesDraftComments = (jdOwnership?.isOwner === true || jdOwnership?.isDelegate === true) && latestIsPrivateDraft;
  const [draftComments, setDraftComments] = useState([]);
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const unsubmittedFeedbackRef = useRef(0);
  const editorRootRef = useRef(null);
  useEffect(() => {
    if (!seesDraftComments || !jobId) { setDraftComments([]); return undefined; }
    let cancelled = false;
    getDraftFeedback(jobId)
      .then((data) => { if (!cancelled) setDraftComments(data?.comments || []); })
      .catch(() => { if (!cancelled) setDraftComments([]); });   // the page works without them
    return () => { cancelled = true; };
  }, [jobId, seesDraftComments, headerVersionData?.sb_unique_id]);
  const handleResolveComment = async (feedbackId) => {
    setFeedbackBusy(true);
    try {
      const data = await resolveDraftFeedback(feedbackId);
      setDraftComments(data?.comments || []);
      SbNotification.success(COMMENT_RESOLVED_TOAST);
    } catch (err) {
      const failure = getFeedbackError(err);
      SbNotification.error('Comment not resolved', failure.message);
      if (failure.reload) await reloadAfterControlChange();
    } finally {
      setFeedbackBusy(false);
    }
  };
  const handleFeedbackRefused = async (refusal) => {
    unsubmittedFeedbackRef.current = 0;
    setCompareViewSelection('');
    setSelectedJdTab(JD_TABS.JOB_DESCRIPTION);
    if (refusal?.reload) await reloadAfterControlChange();
  };

  const handleShare = (shareEmail, name) => runShareChange(
    () => shareJdDrafts(jobId, shareEmail), getSharedToast(name || shareEmail));
  const handleUnshare = (shareEmail, name) => Modal.confirm({
    ...getStopSharingConfirm(name || shareEmail),
    onOk: () => runShareChange(() => unshareJdDrafts(jobId, shareEmail), getUnsharedToast(name || shareEmail)),
  });

  // A write refused NOT_HOLDER (a stale page: someone else holds the JD now): say so and reload the JD as it
  // now is, so the current owner shows
  const reloadAfterNotHolder = async () => {
    SbNotification.error(NOT_HOLDER_TITLE, NOT_HOLDER_MESSAGE);
    lastSavedDraftRef.current = null;
    handleCancelClick();
    setValue('');
    await refreshData();
  };

  // The editor's Cancel: with changes, ask first (Discard changes / Keep editing / Save as Draft);
  // without, just close.
  const handleEditorCancel = () => {
    if (shouldConfirmCancel(value, editableMarkdown)) {
      setIsCancelConfirmOpen(true);
      return;
    }
    handleCancelClick();
    setValue('');
  };

  const handleDiscardChanges = () => {
    setIsCancelConfirmOpen(false);
    handleCancelClick();
    setValue('');
  };

  // Save as Draft: the server decides first save (new version) or overwrite (same version) and returns the
  // draft's id, version and timestamp; the page moves to that id and keeps the values for the next save.
  const handleSaveDraft = async () => {
    setIsCancelConfirmOpen(false);
    if (!editorRules.canSaveDraft || isSavingDraft || !selectedVersionData) return;
    setIsSavingDraft(true);
    try {
      const saved = await saveRevisionDraft(buildDraftSavePayload({
        jobId: selectedVersionData.job_id,
        content: value,
        staleCheck: getStaleCheckValues(latestVersionRow, lastSavedDraftRef.current),
      }));
      lastSavedDraftRef.current = pickSavedDraft(saved);
      SbNotification.success(getDraftSavedToast(saved));
      setIsEditable(false);
      setValue('');
      if (saved?.sb_unique_id) {
        navigate(`/jobs-library/job-details/${jobId}/${saved.sb_unique_id}`, { replace: true });
      }
      await refreshData(saved?.sb_unique_id);
    } catch (err) {
      if (isNotHolderError(err)) {
        await reloadAfterNotHolder();
        return;
      }
      const failure = getDraftSaveError(err);
      if (failure.isStale) {
        offerReload(DRAFT_SAVE_FAILED_TITLE, failure.message);
      } else {
        SbNotification.error(DRAFT_SAVE_FAILED_TITLE, failure.message);
      }
    } finally {
      setIsSavingDraft(false);
    }
  };

  // Called by SbJdHumanEditorV2 after it successfully saves + submits for approval
  // newUniqueId is the ID of the newly created PENDING_APPROVAL version
  const handleHumanEditorSave = async (newUniqueId) => {
    setIsHumanEditing(false);
    setSelectedApprover(null);
    await refreshData(newUniqueId);
  };


  // The flag-off editor's Send for Approval: the legacy edit, which saves a private draft (Save as Draft
  // design, section 13). With the creator-approver flag on, Send opens the Send for Approval page instead.
  const handleSaveClick = async () => {
    analytics.trackSaveClick({ from: ANALYTICS_FROM, email, description: JD_EVENTS.SAVE_CLICK });
    try {
      setIsSubmitting(true);
      const markdownContent = value || editableMarkdown;
      const saved = await saveEditedJd(markdownContent);
      if (saved?.draft_action) SbNotification.success(getDraftSavedToast(saved));
      await refreshData();
      setIsEditable(false);
      setValue('');
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      if (isNotHolderError(error)) {
        await reloadAfterNotHolder();
        return;
      }
      console.error('Error saving job description:', error);
      Modal.error({ title: 'Submission failed. Check network logs.' });
    }
  };

  // ── Send for Approval / Publish (Save as Draft design, sections 4, 5 and 12; wireframe) ──────────────
  // From the editor the edits are saved and sent or published in one request (/approval/save_revision);
  // from the main page the latest version goes as it is (/approval/submit, /self_publish_jd). Every request
  // carries the stale-check values, and the page moves to the ids the server returns.
  const finishSendOrPublish = async (response, toast) => {
    lastSavedDraftRef.current = null;
    setAssignApproverFrom(null);
    setPublishBoxFrom(null);
    SbNotification.success(toast);
    setIsEditable(false);
    setValue('');
    if (response?.sb_unique_id) {
      navigate(`/jobs-library/job-details/${jobId}/${response.sb_unique_id}`, { replace: true });
    }
    await refreshData(response?.sb_unique_id);
  };

  const failSendOrPublish = async (err, action) => {
    const failure = getSendOrPublishError(err, action);
    const title = action === 'publish' ? PUBLISH_FAILED_TITLE : SEND_FAILED_TITLE;
    setAssignApproverFrom(null);
    setPublishBoxFrom(null);
    if (failure.reason === NOT_HOLDER) {
      await reloadAfterNotHolder();
    } else if (failure.savedDraft) {
      // the edits were saved as a private draft, but not sent / published: show that draft
      lastSavedDraftRef.current = failure.savedDraft;
      SbNotification.error(title, failure.message);
      setIsEditable(false);
      setValue('');
      navigate(`/jobs-library/job-details/${jobId}/${failure.savedDraft.sb_unique_id}`, { replace: true });
      await refreshData(failure.savedDraft.sb_unique_id);
    } else if (failure.isStale) {
      offerReload(title, failure.message);
    } else {
      SbNotification.error(title, failure.message);
    }
  };

  // Send on the Send for Approval page (editor: save + send in one request; main page: the latest as it is)
  const handleSendToApprover = async (approver) => {
    if (!approver || isSubmitting || !selectedVersionData) return;
    analytics.trackSubmitForApproval({ from: ANALYTICS_FROM, email, description: JD_EVENTS.SUBMIT_FOR_APPROVAL });
    const fromEditor = assignApproverFrom === ACTION_FROM.EDITOR;
    const staleCheck = getStaleCheckValues(latestVersionRow, lastSavedDraftRef.current);
    const draftStatusBefore = latestVersionRow?.jd_status;
    setIsSubmitting(true);
    try {
      const response = fromEditor
        ? await saveRevisionDraft(buildEditorSendPayload({
          jobId: selectedVersionData.job_id, content: value, staleCheck, approver,
        }))
        : await submitForApproval(staleCheck.latest_sb_unique_id, approver, staleCheck);
      await finishSendOrPublish(response, getSentToast(hadSavedDraft(draftStatusBefore)));
    } catch (err) {
      await failSendOrPublish(err, 'send');
    } finally {
      setIsSubmitting(false);
    }
  };

  // "Yes, publish now" (editor: save + publish in one request; main page: the latest as it is)
  const executePublish = async (from) => {
    if (isSubmitting || !selectedVersionData) return;
    const staleCheck = getStaleCheckValues(latestVersionRow, lastSavedDraftRef.current);
    const draftStatusBefore = latestVersionRow?.jd_status;
    setIsSubmitting(true);
    try {
      const response = from === ACTION_FROM.EDITOR
        ? await saveRevisionDraft(buildEditorPublishPayload({ jobId: selectedVersionData.job_id, content: value, staleCheck }))
        : await selfPublishJd(buildSelfPublishPayload({ jobId: selectedVersionData.job_id, staleCheck }));
      await finishSendOrPublish(response, getPublishedToast(hadSavedDraft(draftStatusBefore)));
    } catch (err) {
      await failSendOrPublish(err, 'publish');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openConfirmationModal = (message, actionText, actionFunction) => {
    setConfirmation({
      show: true,
      message,
      actionText,
      action: actionFunction,
    });
  };

  // HR Manager Publish always opens "Publish without a review?" (every time, no "Don't ask again")
  const handlePublishClick = () => {
    analytics.trackSubmitForApproval({ from: ANALYTICS_FROM, email, description: JD_EVENTS.SUBMIT_FOR_APPROVAL });
    if (!selectedVersionData) return;
    setPublishBoxFrom(isEditable ? ACTION_FROM.EDITOR : ACTION_FROM.MAIN);
  };

  // Send for Approval: the shared Send for Approval page, wherever it is clicked from
  const handleSendClick = () => {
    if (!selectedVersionData) return;
    setAssignApproverFrom(isEditable ? ACTION_FROM.EDITOR : ACTION_FROM.MAIN);
  };

  const handlePublishBoxAction = (action) => {
    const from = publishBoxFrom;
    setPublishBoxFrom(null);   // Cancel only closes the box
    if (action === PUBLISH_BOX_ACTION.SEND) setAssignApproverFrom(from);
    else if (action === PUBLISH_BOX_ACTION.SAVE_DRAFT) handleSaveDraft();
    else if (action === PUBLISH_BOX_ACTION.PUBLISH) executePublish(from);
  };

  const handleUnPublish = () => {
    analytics.trackUnPublish({ from: ANALYTICS_FROM, email, description: JD_EVENTS.UNPUBLISH_JD });
    return openConfirmationModal(
      getUnpublishConfirmMessage(jdOwnership),
      'Unpublish',
      async () => {
        const result = await unPublishJd();
        if (result?.notHolder) SbNotification.error(NOT_HOLDER_TITLE, NOT_HOLDER_MESSAGE);
      },
    );
  };

  const handleConfirmationAction = () => {
    confirmation.action?.();
    setConfirmation(prev => ({ ...prev, show: false }));
  };

  const handleConfirmationClose = () => setConfirmation(prev => ({ ...prev, show: false }));

  // antd Select onChange delivers value directly (not event)

  const handleVersionChange = newVersion => {
    analytics.trackVersionChange({ from: ANALYTICS_FROM, email, description: JD_EVENTS.VERSION_CHANGE });
    setSelectedJdVersion(newVersion);
    const matchesVersion = v => String(v.version) === String(newVersion);
    const versionInfo = versionSelectSource.find(matchesVersion) || versionData.find(matchesVersion);
    if (!versionInfo) return;
    navigate(`/jobs-library/job-details/${jobId}/${versionInfo.sb_unique_id}`, { replace: true });
    handleSbUniqueIdChange(versionInfo.sb_unique_id);
  };

  const handleDiffClick = type => {
    // V1 tracked only the difference view; 'summary' is the custom-compare entry.
    if (type === 'difference') {
      analytics.trackDifferenceChecker({ from: ANALYTICS_FROM, email, description: JD_EVENTS.DIFFERENCE_CHECKER });
    } else {
      analytics.trackCustomCompare({ from: ANALYTICS_FROM, email, description: JD_EVENTS.CUSTOM_COMPARE });
    }
    setSelectedJdTab(JD_TABS.JOB_COMPARE);
    setCompareViewSelection(type);
    setIsEditable(false);
  };

  const handleCloseButton = () => {
    // Closing Draft Feedback with comments not submitted asks first (they live only in the view)
    if (unsubmittedFeedbackRef.current > 0 && selectedJdTab === JD_TABS.JOB_COMPARE) {
      Modal.confirm({
        ...getDiscardConfirm(unsubmittedFeedbackRef.current),
        onOk: () => { unsubmittedFeedbackRef.current = 0; handleCloseButton(); },
      });
      return;
    }
    analytics.trackCloseJobDetailsPage({ from: ANALYTICS_FROM, email, description: JD_EVENTS.CLOSE_JOB_DETAILS_PAGE });
    if (selectedJob && selectedJdTab === JD_TABS.JOB_COMPARE) {
      setCompareViewSelection('');
      setSelectedJdTab(JD_TABS.JOB_DESCRIPTION);
    } else {
      setSelectedJob(null);
      navigate('/jobs-library', { state: {} });
    }
  };

  // ── Sync job when URL jobId changes (notification nav) or on page refresh ──
  // Set a minimal sentinel so the hook's fetchVersions does ONE API call (no double-fetch)
  useEffect(() => {
    if (jobId && sbUniqueId && (!selectedJob || selectedJob.job_id !== jobId)) {
      setSelectedJob({ job_id: jobId, sb_unique_id: sbUniqueId });
    }
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEditable && editorRef.current) {
      const editor = editorRef.current.getEditor?.();
      if (editor?.root) {
        const decodedHtml = decodeHtmlString(editableMarkdown);
        editor.root.innerHTML = decodedHtml;
        setValue(decodedHtml);
      }
    }
  }, [isEditable, editableMarkdown]);

  // The content under a human-edited draft (the version it grew from), for the editor's button rules:
  // editing the draft back to exactly that version disables every button. Fetched only while editing one.
  useEffect(() => {
    if (!isEditable || !editingHumanDraft || !previousStoredId) {
      setPreviousContent(null);
      return undefined;
    }
    let cancelled = false;
    getSBJobDescription(previousStoredId, email)
      .then((res) => { if (!cancelled) setPreviousContent(res?.job_description ?? null); })
      .catch(() => { if (!cancelled) setPreviousContent(null); });
    return () => { cancelled = true; };
  }, [isEditable, editingHumanDraft, previousStoredId, email]);

  // ── Loading guards ────────────────────────────────────────────────────────
  const pageState = getPageState({ selectedJob, versionData, loading, error });
  if (viewMode !== 'compare' && pageState === PAGE_STATE.ERROR) {
    return (
      <PageSpinnerStyled>
        <SbEmpty description={error}>
          <SbButton buttonType="primary" onClick={() => navigate('/jobs-library')}>
            Back to Jobs Library
          </SbButton>
        </SbEmpty>
      </PageSpinnerStyled>
    );
  }
  if (viewMode !== 'compare' && pageState === PAGE_STATE.SPINNER) {
    return (
      <PageSpinnerStyled>
        <SbSpin size="large" />
      </PageSpinnerStyled>
    );
  }

  // When navigated here with viewMode='compare', always show compare regardless of store state
  const activeSubTab = viewMode === 'compare' ? JD_TABS.JOB_COMPARE : selectedJdTab;

  // ── Toolbar: whether toolbar icon-buttons are visible ────────────────────
  const showToolbar = !isEditable && !loading && !isHumanEditing;

  // Mirrors the mode condition inside SbDiffCheckerV3's initializeView so the
  // button's tooltip matches the mode the tool will actually open in. Reads only
  // already-loaded page data — no additional fetch.
  const opensInApproverView =
    mongoWorkflowStatus === WORKFLOW_STATUS.PENDING_APPROVAL &&
    reviewHistory?.[reviewHistory.length - 1]?.assigned_approver_email === email;

  // ── Draft bar / info line (at most one; hidden while editing or comparing) ──
  // Whoever may see the drafts (the owner, from the backend) gets the private-draft bar on a draft; everyone
  // else, while the JD is in draft, the line naming who is working on it. The versions shown are the
  // backend's: everyone else's list has no draft, so their latest row IS the public version.
  const draftNotice = getDraftNotice({
    familyStatus: jdOwnership?.familyStatus,
    viewedStatus: selectedVersionData?.jd_status ?? sbJdStatus,
    isLatest: isViewingLatestVersion,
    canSeeDrafts: jdOwnership?.isOwner === true || jdOwnership?.isDelegate === true || jdOwnership?.isSharedWith === true,
    // the read-only bar for someone the drafts are shared with (never the owner or delegate)
    isSharedWith: isSharedPerson,
    // "X has given comments on this draft – open the editor to view them" (owner and delegate)
    commentAuthors: seesDraftComments ? getCommentAuthors(draftComments) : [],
    ownerName: jdOwnership?.ownerName || currentOwner,
    publicVersion: (jdOwnership?.isOwner || jdOwnership?.isDelegate || jdOwnership?.isSharedWith
      ? getLatestNonDraftRow(versionData) : getLatestVersionRow(versionData))?.version,
    latestVersion: getLatestVersionRow(versionData)?.version,
  });

  // ── JD date/owner line ────────────────────────────────────────────────────
  const modifiedLine = (() => {
    const timeData = selectedVersionData?.last_updated_time_stamp
      ? formatJdDate(selectedVersionData.last_updated_time_stamp)
      : null;
    return timeData ? `Modified on: ${timeData}` : 'Draft saved';
  })();

  // Job Details sits in the same card as the Jobs Library landing page; the
  // compare view (viewMode === 'compare') keeps its original plain wrapper.
  const PageWrapper = viewMode === undefined ? PageContainerStyled : LayoutWrapperStyled;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <PageWrapper>

        <SbModal open={showToast} onCancel={() => setShowToast(false)}>{toastMessage}</SbModal>

        {/* ── Confirmation Modal ─────────────────────────────────────────── */}
        <SbModal
          open={confirmation.show}
          onCancel={handleConfirmationClose}
          wrapClassName="jd-confirm-modal"
          footer={[
            <SbButton
              key="cancel"
              buttonType="secondary"
              onClick={handleConfirmationClose}
            >
              Cancel
            </SbButton>,
            <SbButton
              key="confirm"
              buttonType="primary"
              onClick={handleConfirmationAction}
            >
              {confirmation.actionText}
            </SbButton>,
          ]}
        >
          {confirmation.message}
        </SbModal>

        {/* ── Editor Cancel popup (wireframe): only when something changed; Close / Esc does nothing ── */}
        <SbModal
          open={isCancelConfirmOpen}
          title={DRAFT_CANCEL_CONFIRM.title}
          closable={false}
          maskClosable={false}
          keyboard={false}
          wrapClassName="jd-confirm-modal"
          footer={[
            <SbButton key="discard" buttonType="secondary" onClick={handleDiscardChanges}>
              {DRAFT_CANCEL_CONFIRM.discardLabel}
            </SbButton>,
            <SbButton key="keep" buttonType="secondary" onClick={() => setIsCancelConfirmOpen(false)}>
              {DRAFT_CANCEL_CONFIRM.keepEditingLabel}
            </SbButton>,
            <SbTooltip key="draft" content={editorRules.canSaveDraft ? TOOLTIP_SAVE_AS_DRAFT : TOOLTIP_MAKE_CHANGES_FIRST}>
              <TooltipTriggerStyled>
                <SbButton
                  buttonType="primary"
                  loading={isSavingDraft}
                  disabled={!editorRules.canSaveDraft || isSavingDraft}
                  onClick={handleSaveDraft}
                >
                  {DRAFT_CANCEL_CONFIRM.saveDraftLabel}
                </SbButton>
              </TooltipTriggerStyled>
            </SbTooltip>,
          ]}
        >
          {DRAFT_CANCEL_CONFIRM.body}
        </SbModal>

        {/* ── The shared Send for Approval page (wireframe): from the editor, the main page or the publish box ── */}
        <DelegatePickerModal
          open={delegatePickerOpen}
          jobId={jobId}
          delegating={delegationBusy}
          onCancel={() => setDelegatePickerOpen(false)}
          onDelegate={handleDelegate}
          onRefused={async (refusal) => {
            SbNotification.error(DELEGATION_FAILED_TITLE, refusal.message);
            await reloadAfterControlChange();
          }}
        />
        <SharePickerModal
          open={sharePickerOpen}
          jobId={jobId}
          sharing={sharingBusy}
          onCancel={() => setSharePickerOpen(false)}
          onShare={handleShare}
          onRefused={async (refusal) => {
            SbNotification.error(SHARE_FAILED_TITLE, refusal.message);
            setSharePickerOpen(false);
            await reloadAfterControlChange();
          }}
        />
        <AssignApproverModal
          open={!!assignApproverFrom}
          jobId={jobId}
          jdTitle={headerVersionData?.job_title_for_jd ?? headerVersionData?.job_title ?? ''}
          ownerEmail={rootOwnerEmail}
          reassignedApproverEmail={reassignedApproverEmail}
          sending={isSubmitting}
          onCancel={() => setAssignApproverFrom(null)}
          onSend={handleSendToApprover}
        />

        {/* ── "Publish without a review?" (wireframe): every HR Manager Publish; Cancel closes it, Close / Esc do nothing ── */}
        <PublishWithoutReviewModal
          open={!!publishBoxFrom}
          fromEditor={publishBoxFrom === ACTION_FROM.EDITOR}
          busy={isSubmitting || isSavingDraft}
          saveDraftDisabled={!editorRules.canSaveDraft}
          onAction={handlePublishBoxAction}
        />

        {/* ── Job title + metadata ───────────────────────────────────────── */}
        {viewMode === undefined && (
          <PageHeader
            title={`Job Details: ${headerVersionData?.job_title_for_jd ?? headerVersionData?.job_title ?? ''}`}
            subtitle={
              <Paragraph style={{ margin: 0 }} ellipsis={{ rows: 2, expandable: true, symbol: 'more' }} type="secondary">
                <strong>{headerVersionData?.sb_requisition_number || 'N/A'}</strong> -{' '}
                {headerVersionData?.job_title_for_jd ?? headerVersionData?.job_title ?? 'N/A'}
                {headerVersionData?.business_unit?.length > 0 && (
                  <>
                    ,{' '}<strong>{config?.business_unit?.label ?? 'Business Unit'}:</strong> {headerVersionData?.business_unit}
                  </>
                )}
                {headerVersionData?.function?.length > 0 && (
                  <>
                    ,{' '}<strong>{config?.function?.label ?? 'Function'}:</strong> {headerVersionData?.function}
                  </>
                )}
                {headerVersionData?.sub_function?.length > 0 && (
                  <>
                    ,{' '}<strong>{config?.sub_function?.label ?? 'Sub-Function'}:</strong> {headerVersionData?.sub_function}
                  </>
                )}
                {headerVersionData?.specialty?.length > 0 && (
                  <>
                    ,{' '}<strong>{config?.specialty?.label ?? 'Specialty'}:</strong> {headerVersionData?.specialty}
                  </>
                )}
              </Paragraph>
            }
          />
        )}

        {/* ── Main tabs ─────────────────────────────────────────────────── */}
        <TabWrapper>
          <SbTabs
            size="large"
            activeKey={activeTab}
            onChange={handleTabClick}
            tabBarExtraContent={
              <SbButton
                buttonType="primary"
                icon={<CloseOutlined />}
                onClick={handleCloseButton}
              />
            }
            items={[
              {
                key: 'jd',
                label: 'Jobs Library',
                children: (
                  <div>
                    {/* ── Job Description view ──────────────────────────── */}
                    {viewMode === undefined && activeSubTab === JD_TABS.JOB_DESCRIPTION && (
                      <PageWrapperStyled>
                        <WhiteContainerStyled $isFullscreen={isFullscreen}>

                          {/* Grey header bar */}
                          {!loading && !isHumanEditing && (
                            <HeaderSectionStyled>
                              {/* Left: "Edit JD" heading (edit mode only) + version selector + metadata */}
                              <HeaderLeftStyled>
                                {isEditable && (
                                  <EditModeHeadingStyled>Edit JD Mode</EditModeHeadingStyled>
                                )}
                                <VersionBlockStyled>
                                  {/* Wireframe grey bar: the version select over "Modified on", the people beside them */}
                                  <VersionMetaStyled>
                                    {loading ? (
                                      <SbSpin size="small" />
                                    ) : (
                                      <Permission resource="jobs_library-version" actions={['view']}>
                                        {/* Version Dropdown (§5.4) */}
                                        <SbTooltip
                                          content={TOOLTIP_VERSION_SELECTOR}
                                          disabled={!canViewVersion}
                                        >
                                          <TooltipTriggerStyled>
                                            <SbSelect
                                              value={String(selectedJdVersion)}
                                              onChange={handleVersionChange}
                                              showSearch={false}
                                              options={versionSelectOptions}
                                              optionRender={renderVersionOption}
                                            />
                                          </TooltipTriggerStyled>
                                        </SbTooltip>
                                      </Permission>
                                    )}
                                    {selectedJdVersion && versionData.length > 0 && (
                                      <Text type="secondary">
                                        {/* Modified On info tooltip (§5.4) */}
                                        <SbInfoTooltip content={TOOLTIP_MODIFIED_ON}>
                                          {modifiedLine}
                                        </SbInfoTooltip>
                                      </Text>
                                    )}
                                  </VersionMetaStyled>
                                  {selectedJdVersion && versionData.length > 0 && (
                                    <VersionPeopleStyled>
                                      {/* The JD's permanent owner (approval record), or Unclaimed; falls back to the
                                          version author when the response carries no ownership facts */}
                                      <JdCurrentOwnerRow jdOwnership={jdOwnership} fallbackName={currentOwner} />
                                      <JdDelegatedToRow
                                        jdOwnership={jdOwnership}
                                        canManage={canEditJd}
                                        isEditing={isEditable || isHumanEditing}
                                        busy={delegationBusy}
                                        onAdd={() => setDelegatePickerOpen(true)}
                                        onTakeBack={handleTakeBack}
                                        onRelinquish={handleRelinquish}
                                      />
                                      <JdSharedWithRow
                                        jdOwnership={jdOwnership}
                                        viewerEmail={email}
                                        canManage={canEditJd}
                                        isEditing={isEditable || isHumanEditing}
                                        busy={sharingBusy}
                                        onAdd={() => setSharePickerOpen(true)}
                                        onRemove={handleUnshare}
                                      />
                                    </VersionPeopleStyled>
                                  )}
                                </VersionBlockStyled>
                              </HeaderLeftStyled>


                              {/* Right: toolbar icon buttons + action buttons */}
                              {showToolbar && (
                                <Space align='center' ref={toolbarRef}>
                                  {sbJdStatus === JD_STATUSES.PENDING_APPROVAL && (
                                    <StatusBadgeWrapperStyled>
                                      In review for approval{assignedApproverName ? ` by ${assignedApproverName}` : ''}
                                    </StatusBadgeWrapperStyled>
                                  )}
                                  {/* Comparison Summary (§5.2) */}
                                  <Permission resource="jobs_library" actions={['compare_data']}>
                                    <SbTooltip
                                      content={TOOLTIP_COMPARISON_SUMMARY}
                                      disabled={!canCompareData}
                                    >
                                      <TooltipTriggerStyled>
                                        <SbButton
                                          icon={<ComparisonsummaryIcon />}
                                          onClick={() => handleDiffClick('summary')}
                                        />
                                      </TooltipTriggerStyled>
                                    </SbTooltip>
                                  </Permission>
                                  <Permission resource="jobs_library" actions={['compare_data']}>

                                    {/* Difference Checker (§5.2) */}
                                    <SbTooltip
                                      content={isSharedPerson && latestIsPrivateDraft ? TOOLTIP_DIFF_FEEDBACK
                                        : opensInApproverView ? TOOLTIP_DIFF_APPROVE : TOOLTIP_DIFF_COMPARE}
                                      disabled={!canCompareData}
                                    >
                                      <TooltipTriggerStyled>
                                        <SbButton
                                          icon={<DifferenceCheckerIcon />}
                                          disabled={!!blurredJD}
                                          onClick={() => !blurredJD && handleDiffClick('difference')}
                                        />
                                      </TooltipTriggerStyled>
                                    </SbTooltip>
                                  </Permission>
                                  {/* Edit + Revise (wireframe): only for whoever may act on the JD (its holder, or anyone on an
                                      Unclaimed JD), never while PUBLISHED or PENDING_APPROVAL; disabled otherwise when not allowed */}
                                  {showsEditAndRevise({ jdOwnership, familyStatus: latestFamilyStatus, isLockedByOther }) && (
                                    <>
                                      {/* Edit Icon — three variants per PRD §5.2 */}
                                      <Permission resource="jobs_library-job_details" actions={['edit']}>
                                        <SbTooltip
                                          content={
                                            blockedReason('can_edit') ||
                                            getUnclaimedTooltip('can_edit', jdOwnership) ||
                                            (isLockedByOther
                                              ? TOOLTIP_EDIT_LOCKED_BY_OTHER
                                              : latestFamilyStatus === JD_STATUSES.NEEDS_REVISION
                                                ? TOOLTIP_EDIT_SURGICAL
                                                : TOOLTIP_EDIT_DEFAULT)
                                          }
                                          disabled={!canEditJd}
                                        >
                                          <TooltipTriggerStyled>
                                            <SbButton
                                              icon={<EditIcon />}
                                              disabled={
                                                !!blockedReason('can_edit') ||
                                                (isLockedByOther && (!hasApproveEntitlement || latestFamilyStatus === JD_STATUSES.NEEDS_REVISION)) ||
                                                (isNewDiffCheckerFeatureEnabled() && (
                                                  ![...JD_STATUSES_EDITABLE, JD_STATUSES.NEEDS_REVISION].includes(latestFamilyStatus) ||
                                                  !isViewingLatestVersion
                                                ))
                                              }
                                              onClick={handleEditClick}
                                            />
                                          </TooltipTriggerStyled>
                                        </SbTooltip>
                                      </Permission>

                                      {/* Revise Icon (§5.2) */}
                                      <Permission resource="jobs_library-job_details" actions={['revise']}>
                                        <SbTooltip
                                          content={
                                            latestFamilyStatus === JD_STATUSES.NEEDS_REVISION
                                              ? TOOLTIP_REVISE_IN_REVIEW
                                              : blockedReason('can_revise') || getUnclaimedTooltip('can_revise', jdOwnership) ||
                                                getReviseTooltip(latestFamilyStatus, hasApproveEntitlement)
                                          }
                                          disabled={!canReviseJd}
                                        >
                                          <TooltipTriggerStyled>
                                            <SbButton
                                              icon={<ReviseIcon />}
                                              disabled={
                                                !!blockedReason('can_revise') || isLockedByOther ||
                                                latestFamilyStatus === JD_STATUSES.NEEDS_REVISION
                                              }
                                              onClick={() => {
                                                analytics.trackReviseJD({ from: ANALYTICS_FROM, email, description: JD_EVENTS.REVISE_JD });
                                                // Revise acts on the latest version, the one on screen: with manual edits, warn first
                                                confirmReviseIfManuallyEdited(jdInputType, () => {
                                                  setJdActionSelection(JD_ACTION.REVISE);
                                                  toggleDrawer();
                                                }, Modal.confirm);
                                              }}
                                            />
                                          </TooltipTriggerStyled>
                                        </SbTooltip>
                                      </Permission>
                                    </>
                                  )}

                                  {/* Download (§5.2) */}
                                  <Permission resource="jobs_library-job_details" actions={['view']}>
                                    {blurredJD ? (
                                      <SbTooltip
                                        content={TOOLTIP_DOWNLOAD_JD}
                                        disabled={!canViewJd}
                                      >
                                        <TooltipTriggerStyled>
                                          <SbButton icon={<DownloadIcon />} disabled />
                                        </TooltipTriggerStyled>
                                      </SbTooltip>
                                    ) : jdPublished ? (
                                      <Space>
                                        <SbTooltip
                                          content={TOOLTIP_DOWNLOAD_PDF}
                                          disabled={!canViewJd}
                                        >
                                          <TooltipTriggerStyled>
                                            <a
                                              href={publishedJDfiles?.pdfUrl}
                                              download
                                              onClick={() => {
                                                analytics.trackDownloadPDF({ from: ANALYTICS_FROM, email, description: JD_EVENTS.DOWNLOAD_PDF });
                                                reportJdDownload(jobId, shownSbUniqueId, 'pdf');
                                              }}
                                              style={{ pointerEvents: publishedJDfiles?.pdfUrl ? 'auto' : 'none' }}
                                            >
                                              <SbButton
                                                icon={<PdfIcon />}
                                                disabled={!publishedJDfiles?.pdfUrl || loading}
                                              />
                                            </a>
                                          </TooltipTriggerStyled>
                                        </SbTooltip>
                                        <SbTooltip
                                          content={TOOLTIP_DOWNLOAD_DOCX}
                                          disabled={!canViewJd}
                                        >
                                          <TooltipTriggerStyled>
                                            <a
                                              href={publishedJDfiles?.docxUrl}
                                              download
                                              onClick={() => {
                                                analytics.trackDownloadDOCX({ from: ANALYTICS_FROM, email, description: JD_EVENTS.DOWNLOAD_DOCX });
                                                reportJdDownload(jobId, shownSbUniqueId, 'docx');
                                              }}
                                              style={{ pointerEvents: publishedJDfiles?.docxUrl ? 'auto' : 'none' }}
                                            >
                                              <SbButton
                                                icon={<DocxIcon />}
                                                disabled={!publishedJDfiles?.docxUrl || loading}
                                              />
                                            </a>
                                          </TooltipTriggerStyled>
                                        </SbTooltip>
                                      </Space>
                                    ) : (
                                      <SbTooltip
                                        content={TOOLTIP_DOWNLOAD_UNPUBLISHED}
                                        disabled={!canViewJd}
                                      >
                                        <TooltipTriggerStyled>
                                          <SbDownloadMarkdownPdfV2
                                            markdownContent={editableMarkdown}
                                            jobData={selectedJob}
                                            onDownload={() => reportJdDownload(jobId, shownSbUniqueId, 'pdf')}
                                          />
                                        </TooltipTriggerStyled>
                                      </SbTooltip>
                                    )}
                                  </Permission>

                                  {/* Fullscreen toggle */}
                                  <SbTooltip content={isFullscreen ? TOOLTIP_FULLSCREEN_EXIT : TOOLTIP_FULLSCREEN_ENTER}>
                                    <TooltipTriggerStyled>
                                      <SbButton
                                        icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                                        onClick={handleFullscreenToggle}
                                      />
                                    </TooltipTriggerStyled>
                                  </SbTooltip>

                                  {/* Send for Approval (both roles; wireframe toolbar) — Unpublish takes this slot when PUBLISHED.
                                      Opens the shared Send for Approval page. Disabled only when the latest version is
                                      UNPUBLISHED and unchanged since it was published (a NEW v1.00, Revise results and drafts
                                      go as they are), during a review, or when the backend blocks it. */}
                                  {isNewDiffCheckerFeatureEnabled() &&
                                    latestFamilyStatus !== JD_STATUSES.PENDING_APPROVAL &&
                                    latestFamilyStatus !== JD_STATUSES.PUBLISHED && (
                                      <SbTooltip
                                        content={
                                          latestFamilyStatus === JD_STATUSES.NEEDS_REVISION
                                            ? TOOLTIP_SEND_IN_REVIEW
                                            : blockedReason('can_submit') ||
                                              (isUnchangedUnpublished(latestFamilyStatus) ? TOOLTIP_SEND_UNCHANGED : TOOLTIP_SEND_MAIN)
                                        }
                                      >
                                        <TooltipTriggerStyled>
                                          <SbButton
                                            buttonType={hasApproveEntitlement ? 'secondary' : 'primary'}
                                            loading={isSubmitting && assignApproverFrom === ACTION_FROM.MAIN}
                                            disabled={
                                              latestFamilyStatus === JD_STATUSES.NEEDS_REVISION ||
                                              isUnchangedUnpublished(latestFamilyStatus) ||
                                              isLockedByOther ||
                                              isSubmitting ||
                                              !!blockedReason('can_submit')
                                            }
                                            onClick={handleSendClick}
                                          >
                                            {SEND_FOR_APPROVAL_LABEL}
                                          </SbButton>
                                        </TooltipTriggerStyled>
                                      </SbTooltip>
                                    )}

                                  {/* Publish: approver entitlement (HR Managers); always opens "Publish without a review?" */}
                                  {hasApproveEntitlement &&
                                    latestFamilyStatus !== JD_STATUSES.PENDING_APPROVAL &&
                                    latestFamilyStatus !== JD_STATUSES.PUBLISHED && (
                                      <Permission resource="jobs_library-job_details" actions={['publish']}>
                                        <SbTooltip
                                          content={
                                            latestFamilyStatus === JD_STATUSES.NEEDS_REVISION
                                              ? TOOLTIP_PUBLISH_IN_REVIEW
                                              : blockedReason('can_self_publish') ||
                                                (isUnchangedUnpublished(latestFamilyStatus) ? TOOLTIP_PUBLISH_UNCHANGED : TOOLTIP_PUBLISH_MAIN)
                                          }
                                          disabled={!canPublishJd}
                                        >
                                          <TooltipTriggerStyled>
                                            <SbButton
                                              buttonType="primary"
                                              loading={isSubmitting && !assignApproverFrom}
                                              disabled={
                                                latestFamilyStatus === JD_STATUSES.NEEDS_REVISION ||
                                                isUnchangedUnpublished(latestFamilyStatus) ||
                                                isSubmitting ||
                                                !!blockedReason('can_self_publish')
                                              }
                                              onClick={handlePublishClick}
                                            >
                                              {PUBLISH_LABEL}
                                            </SbButton>
                                          </TooltipTriggerStyled>
                                        </SbTooltip>
                                      </Permission>
                                    )}

                                  {/* Unpublish: shown when PUBLISHED (§5.3) */}
                                  {latestFamilyStatus === JD_STATUSES.PUBLISHED && (
                                    <Permission resource="jobs_library-job_details" actions={['unpublish']}>
                                      <SbTooltip
                                        content={blockedReason('can_unpublish') || TOOLTIP_UNPUBLISH}
                                        disabled={!canUnpublishJd || latestFamilyStatus !== JD_STATUSES.PUBLISHED}
                                      >
                                        <TooltipTriggerStyled>
                                          <SbButton buttonType="primary" disabled={!!blockedReason('can_unpublish')} onClick={handleUnPublish}>
                                            Unpublish
                                          </SbButton>
                                        </TooltipTriggerStyled>
                                      </SbTooltip>
                                    </Permission>
                                  )}

                                  {/* Publish during review: always disabled — the assigned approver publishes from the
                                      review screen once every change is approved (/approval/review), never from here */}
                                  {hasApproveEntitlement &&
                                    latestFamilyStatus === JD_STATUSES.PENDING_APPROVAL && (
                                      <Permission resource="jobs_library-job_details" actions={['publish']}>
                                        <SbTooltip
                                          content={opensInApproverView ? TOOLTIP_PUBLISH_MID_REVIEW_APPROVER : TOOLTIP_PUBLISH_MID_REVIEW_NOT_OWNER}
                                        >
                                          <TooltipTriggerStyled>
                                            <SbButton buttonType="primary" disabled>
                                              Publish
                                            </SbButton>
                                          </TooltipTriggerStyled>
                                        </SbTooltip>
                                      </Permission>
                                    )}
                                </Space>
                              )}

                              {/* Jobs Library guided tour: toolbar step (handed off from Jobs Library) */}
                              <SbTour
                                key={toolbarTourKey}
                                open={toolbarTourOpen}
                                onClose={endJobsToolbarStep}
                                onFinish={endJobsToolbarStep}
                                indicatorsRender={(current, total) => `${current + 1} / ${total}`}
                                steps={[
                                  {
                                    target: () => toolbarRef.current,
                                    placement: JOB_DETAILS_TOOLBAR_STEP?.placement,
                                    description: (
                                      <TourStep
                                        title={JOB_DETAILS_TOOLBAR_STEP?.title}
                                        description={JOB_DETAILS_TOOLBAR_STEP?.description}
                                      />
                                    ),
                                    nextButtonProps: { children: "Next" },
                                  },
                                ]}
                              />

                              {/* Editor buttons (wireframe): Cancel, Save as Draft (both roles), Send for Approval (both
                                  roles; it opens the Send for Approval page, there is no approver picker here), Publish (HR
                                  Managers; it opens "Publish without a review?"). A button that would store the same content
                                  as the version before it is disabled ("Make changes first"). With the creator-approver flag
                                  off, Send for Approval keeps running the legacy edit (which saves a draft). */}
                              {isEditable && (
                                <Space align="center">
                                  <SbButton buttonType="secondary" onClick={handleEditorCancel}>
                                    Cancel
                                  </SbButton>
                                  <SbTooltip content={editorRules.canSaveDraft ? TOOLTIP_SAVE_AS_DRAFT : TOOLTIP_MAKE_CHANGES_FIRST}>
                                    <TooltipTriggerStyled>
                                      <SbButton
                                        buttonType="secondary"
                                        loading={isSavingDraft}
                                        disabled={!editorRules.canSaveDraft || isSavingDraft || isSubmitting}
                                        onClick={handleSaveDraft}
                                      >
                                        {SAVE_AS_DRAFT_LABEL}
                                      </SbButton>
                                    </TooltipTriggerStyled>
                                  </SbTooltip>
                                  {!isLockedByOther && (isNewDiffCheckerFeatureEnabled() || !hasApproveEntitlement) && (
                                    <SbTooltip content={editorRules.canSendOrPublish ? TOOLTIP_SEND_MAIN : TOOLTIP_MAKE_CHANGES_FIRST}>
                                      <TooltipTriggerStyled>
                                        <SbButton
                                          buttonType={hasApproveEntitlement ? 'secondary' : 'primary'}
                                          loading={isSubmitting && !!assignApproverFrom}
                                          disabled={isSubmitting || isSavingDraft || !editorRules.canSendOrPublish}
                                          onClick={isNewDiffCheckerFeatureEnabled() ? handleSendClick : handleSaveClick}
                                        >
                                          {SEND_FOR_APPROVAL_LABEL}
                                        </SbButton>
                                      </TooltipTriggerStyled>
                                    </SbTooltip>
                                  )}
                                  {hasApproveEntitlement && (
                                    <Permission resource="jobs_library-job_details" actions={['publish']}>
                                      <SbTooltip
                                        content={editorRules.canSendOrPublish ? TOOLTIP_PUBLISH_MAIN : TOOLTIP_MAKE_CHANGES_FIRST}
                                        disabled={!canPublishJd}
                                      >
                                        <TooltipTriggerStyled>
                                          <SbButton
                                            buttonType="primary"
                                            loading={isSubmitting && !assignApproverFrom}
                                            disabled={isSubmitting || isSavingDraft || !editorRules.canSendOrPublish}
                                            onClick={handlePublishClick}
                                          >
                                            {PUBLISH_LABEL}
                                          </SbButton>
                                        </TooltipTriggerStyled>
                                      </SbTooltip>
                                    </Permission>
                                  )}
                                </Space>
                              )}
                            </HeaderSectionStyled>
                          )}

                          {/* Draft bar / info line (Save as Draft design, section 4; wireframe jd-alerts) */}
                          {draftNotice && !loading && !isHumanEditing && !isEditable && (
                            <Alert
                              type={draftNotice.type}
                              showIcon
                              data-testid="draft-notice"
                              style={{ ...DRAFT_NOTICE_STYLES[draftNotice.type], margin: '0 0 8px' }}
                              title={draftNotice.segments.map((segment, i) => {
                                if (segment.lineBreak) return <br key={i} />;
                                return segment.bold
                                  ? <b key={i}>{segment.text}</b>
                                  : <React.Fragment key={i}>{segment.text}</React.Fragment>;
                              })}
                            />
                          )}

                          {/* Edit mode (legacy markdown editor) */}
                          {isEditable && !isHumanEditing && (
                            loading ? (
                              <JdSpinnerStyled>
                                <SbSpin size="large" />
                              </JdSpinnerStyled>
                            ) :
                              <EditModeWrapperStyled $isFullscreen={isFullscreen}>
                                {/* The open comments on the draft, above the editor (wireframe richEditor) */}
                                {seesDraftComments && (
                                  <DraftCommentsPanel
                                    comments={draftComments}
                                    markdown={value || editableMarkdown}
                                    canResolve={isJdHolder(jdOwnership)}
                                    busy={feedbackBusy}
                                    onResolve={handleResolveComment}
                                    editorRootRef={editorRootRef}
                                  />
                                )}
                                <ScrollAreaStyled ref={editorRootRef}>
                                  <CoreMarkdownEditor
                                    initialMarkdown={editableMarkdown}
                                    viewMode={VIEWMODE.EDIT}
                                    onChange={setValue}
                                    ref={editorRef}
                                  />
                                </ScrollAreaStyled>
                              </EditModeWrapperStyled>
                          )}

                          {/* Human / surgical edit mode */}
                          {isHumanEditing && (
                            <div >
                              <SbJdHumanEditorV2
                                editableMarkdown={editableMarkdown}
                                setEditableMarkdown={setEditableMarkdown}
                                onCancel={handleCancelClick}
                                onSave={handleHumanEditorSave}
                                selectedApprover={selectedApprover}
                                setSelectedApprover={setSelectedApprover}
                                assignedApproverName={assignedApproverName}
                                reassignedApproverEmail={reassignedApproverEmail}
                                isSubmitting={isSubmitting}
                              />
                            </div>
                          )}

                          {/* Read-only JD content */}
                          {!isEditable && !isHumanEditing && (
                            loading ? (
                              <JdSpinnerStyled>
                                <SbSpin size="large" />
                              </JdSpinnerStyled>
                            ) :
                              <ScrollableContentStyled $isFullscreen={isFullscreen}>
                                <BlurredContentStyled $blurred={blurredJD}>
                                  {isMarkdown(editableMarkdown) ? (
                                    <CoreMarkdownEditor
                                      initialMarkdown={editableMarkdown}
                                      viewMode={VIEWMODE.VIEW}
                                    />
                                  ) : (
                                    <div
                                      className="job-description-html"
                                      dangerouslySetInnerHTML={{
                                        __html: decodeHtmlString(editableMarkdown),
                                      }}
                                    />
                                  )}
                                </BlurredContentStyled>

                                <DisclaimerStyled>
                                  <Title level={3}>Disclaimer:</Title>
                                  <p>
                                    This job description is provided for illustrative and demonstration
                                    purposes only. It was generated using artificial intelligence technology.
                                    As an AI-generated document, it may not fully reflect current industry
                                    standards, specific organizational needs, or human expertise.
                                  </p>
                                  <p>
                                    Users are encouraged to independently verify the accuracy and
                                    applicability of this job description to their specific situation before
                                    relying on it for any decisions. The information contained herein is not
                                    intended to be, and should not be construed as legal, financial, or
                                    professional advice. This document does not create any binding agreement
                                    or obligation between parties.
                                  </p>
                                  <p>
                                    SkillsBridge.AI disclaims all liability for any errors, omissions,
                                    inaccuracies, or potential biases in this document. Any reliance you
                                    place on such information is strictly at your own risk. Unauthorized use,
                                    disclosure, or distribution is strictly prohibited.
                                  </p>
                                </DisclaimerStyled>

                                {blurredJD && (
                                  <FreeTrialPopupStyled>
                                    <Title level={3}>Subscribe for access to more JDs!</Title>
                                    <p>
                                      Thanks for exploring SkillsBridge.AI! Your organization has reached
                                      the limit for creating free job descriptions. To unlock the full
                                      potential of our platform and view more JDs, a subscription is needed.
                                    </p>
                                    <p>
                                      Don't worry, we're here to help! Our team will be in touch soon to
                                      guide you through the process. Please keep an eye on your email for
                                      our message.
                                    </p>
                                  </FreeTrialPopupStyled>
                                )}
                              </ScrollableContentStyled>
                          )}

                        </WhiteContainerStyled>
                      </PageWrapperStyled>
                    )}

                    {/* ── Compare JDs view ──────────────────────────────── */}
                    {activeSubTab === JD_TABS.JOB_COMPARE && (
                      compareViewSelection === 'difference'
                        ? <SbDiffCheckerV3
                          refreshData={refreshData}
                          currentVersionData={selectedVersionData}
                          editableMarkdown={editableMarkdown}
                          mongoWorkflowStatus={mongoWorkflowStatus}
                          reviewHistory={reviewHistory}
                          draftFeedback={{
                            enabled: isSharedPerson && latestIsPrivateDraft,
                            ownerName: jdOwnership?.ownerName || currentOwner,
                          }}
                          onUnsubmittedChange={(count) => { unsubmittedFeedbackRef.current = count; }}
                          onFeedbackRefused={handleFeedbackRefused}
                        />
                        : <SbJobCompareV2 entryFrom={compareEntryFrom ?? COMPARE_ENTRY.JOB_DETAILS} />
                    )}

                  </div>
                ),
              },
              {
                key: 'skills-bridger',
                label: 'Skills Bridger',
                children: <ComingSoonStyled><Text type="secondary">Coming Soon..</Text></ComingSoonStyled>,
              },
              {
                key: 'learning-catalyst',
                label: 'Learning Catalyst',
                children: <ComingSoonStyled><Text type="secondary">Coming Soon..</Text></ComingSoonStyled>,
              },
              {
                key: 'talent-mobility',
                label: 'Talent Mobility',
                children: <ComingSoonStyled><Text type="secondary">Coming Soon..</Text></ComingSoonStyled>,
              },
            ]}
          />
        </TabWrapper>

      </PageWrapper>

      {/* ── Drawer (portal — outside the layout) ──────────────────────────── */}
      {isDrawerOpen && (
        <SbJdCreationDrawer
          jdActionSelection={jdActionSelection}
          isDrawerOpen={isDrawerOpen}
          toggleDrawer={toggleDrawer}
          setPendingJdId={setPendingJdId}
          initialJobData={selectedJob}
          versionData={versionData}
          selectedJdVersion={selectedJdVersion}
          handleVersionChange={handleVersionChange}
        />
      )}
    </>
  );
};

export default SbJobDetailsV2;
